import { useEffect, useState } from "react";
import { X, PaperPlaneTilt, EnvelopeSimple, Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Forward an already-sent invoice to arbitrary email addresses (accountants, boss, etc.).
 *  Sends the same branded PDF + guest-pay link with an optional custom note. */
export function ForwardInvoiceModal({ open, invoice, onClose, onSent }) {
  const [emails, setEmails] = useState([""]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmails([""]);
    setMessage("");
  }, [open]);

  if (!open || !invoice) return null;

  const updateEmail = (i, val) => setEmails((arr) => arr.map((e, idx) => idx === i ? val : e));
  const addEmail = () => setEmails((arr) => arr.length >= 10 ? arr : [...arr, ""]);
  const removeEmail = (i) => setEmails((arr) => arr.length === 1 ? arr : arr.filter((_, idx) => idx !== i));

  const validate = () => {
    const cleaned = emails.map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (cleaned.length === 0) return "Add at least one email address";
    for (const e of cleaned) {
      if (!e.includes("@") || e.split("@")[1].indexOf(".") === -1) return `"${e}" doesn't look like a valid email`;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSending(true);
    try {
      const cleaned = [...new Set(emails.map((s) => s.trim().toLowerCase()).filter(Boolean))];
      const { data } = await api.post(`/admin/invoices/${invoice.id}/forward`, {
        to_emails: cleaned,
        message: message.trim() || null,
      });
      const failedCount = (data.failed_to || []).length;
      if (failedCount > 0) {
        toast.warning(`Sent to ${data.count}. ${failedCount} failed: ${data.failed_to.map((f) => f.email).join(", ")}`);
      } else {
        toast.success(`Invoice forwarded to ${data.count} ${data.count === 1 ? "address" : "addresses"}`);
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Forward failed");
    } finally {
      setSending(false);
    }
  };

  const balance = Math.max(0, Number(invoice.total_usd || 0) - Number(invoice.paid_amount || 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="forward-invoice-modal">
      <div className="bg-[#0a0a0a] border border-[#FF2D2D] w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Forward invoice</div>
            <div className="font-display text-xl mt-1">{invoice.invoice_number}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close" data-testid="forward-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          <div className="border border-[#262626] bg-[#050505] p-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Sending PDF + pay link for</div>
            <div className="text-sm text-white mt-1 truncate">{invoice.title}</div>
            <div className="text-[11px] text-[#a3a3a3] mt-0.5">
              Billed to {invoice.client_name}{invoice.client_company ? ` · ${invoice.client_company}` : ""} · originally sent to {invoice.client_email}
            </div>
            <div className="text-[11px] mt-2 tabular-nums">
              {balance > 0 ? (
                <span className="text-[#FF2D2D]">Balance due: {formatUSD(balance)}</span>
              ) : (
                <span className="text-[#28C878]">Paid in full · {formatUSD(invoice.total_usd)}</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Recipients *</label>
              <button type="button" onClick={addEmail} disabled={emails.length >= 10}
                data-testid="forward-add-email"
                className="text-[10px] uppercase tracking-wider text-[#FF2D2D] hover:text-white flex items-center gap-1 disabled:opacity-30">
                <Plus size={11} weight="bold" /> Add another
              </button>
            </div>
            <div className="space-y-2">
              {emails.map((val, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <EnvelopeSimple size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3]" />
                    <input type="email" value={val} onChange={(e) => updateEmail(idx, e.target.value)}
                      placeholder="accountant@company.com"
                      data-testid={`forward-email-${idx}`}
                      maxLength={200}
                      className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none" />
                  </div>
                  <button type="button" onClick={() => removeEmail(idx)}
                    disabled={emails.length === 1}
                    data-testid={`forward-remove-${idx}`}
                    className="border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] p-2 disabled:opacity-30 disabled:cursor-not-allowed">
                    <Trash size={11} />
                  </button>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-[#666] mt-1">Up to 10 recipients per forward.</div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Message (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} maxLength={500}
              placeholder="e.g. Hi Sarah, please add this to next week's AP run. Thanks!"
              data-testid="forward-message"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none" />
            <div className="text-[10px] text-[#666] mt-1 text-right">{message.length}/500</div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button type="submit" disabled={sending} data-testid="forward-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
              {sending ? <>Sending…</> : <><PaperPlaneTilt size={12} weight="fill" /> Forward invoice</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
